import type { CatalogContentPatch, CatalogFaqItem } from '../../catalog/types';
import { textHasInternalLeak } from './editorialLeak';

export interface ParsedFichaFile {
    filename: string;
    seoSlug: string;
    nuevoSeoSlug?: string;
    patch: CatalogContentPatch;
    warnings: string[];
    error?: string;
    leak: boolean;
}

const FRONTMATTER_DASH = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;
const FRONTMATTER_HTML = /^<!--([\s\S]*?)-->/;

const SEO_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function readColeccionSlugs(raw: unknown): { slugs?: string[]; error?: string } {
    if (raw == null || raw === '') return {};
    const values = Array.isArray(raw)
        ? raw.map((item) => String(item))
        : parseScalarList(String(raw));
    const slugs: string[] = [];
    for (const value of values) {
        const slug = value.trim();
        if (!slug) continue;
        if (!SEO_SLUG_RE.test(slug)) return { error: 'coleccion_slugs no es válido' };
        if (!slugs.includes(slug)) slugs.push(slug);
    }
    return { slugs };
}

/** Vacío: no hay cambio. Si viene, tiene que ser un slug distinto del actual. */
export function readNuevoSeoSlug(
    raw: string | undefined,
    current: string,
): { slug?: string; error?: string } {
    const value = String(raw ?? '').trim();
    if (!value) return {};
    if (value.length > 200 || !SEO_SLUG_RE.test(value)) {
        return { error: 'nuevo_seo_slug no es válido' };
    }
    if (value === current) return { error: 'nuevo_seo_slug es igual al actual' };
    return { slug: value };
}

function unquote(value: string): string {
    const v = value.trim();
    if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
        return v.slice(1, -1);
    }
    return v;
}

function parseScalarList(raw: string): string[] {
    const v = unquote(raw).trim();
    if (!v) return [];
    const inner = v.startsWith('[') && v.endsWith(']') ? v.slice(1, -1) : v;
    return inner
        .split(/[,;]/)
        .map((s) => unquote(s).trim())
        .filter(Boolean);
}

function parseFrontmatterBlock(block: string): Record<string, string> {
    const attrs: Record<string, string> = {};
    for (const line of block.split(/\r?\n/)) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) continue;
        const idx = trimmed.indexOf(':');
        if (idx < 1) continue;
        const key = trimmed.slice(0, idx).trim().toLowerCase().replace(/-/g, '_');
        attrs[key] = unquote(trimmed.slice(idx + 1));
    }
    return attrs;
}

function splitFrontmatter(text: string): { attrs: Record<string, string>; body: string } {
    const dash = text.match(FRONTMATTER_DASH);
    if (dash) {
        return { attrs: parseFrontmatterBlock(dash[1]), body: text.slice(dash[0].length).trim() };
    }
    const html = text.match(FRONTMATTER_HTML);
    if (html) {
        return { attrs: parseFrontmatterBlock(html[1]), body: text.slice(html[0].length).trim() };
    }
    return { attrs: {}, body: text.trim() };
}

function headingKey(title: string): 'editorial' | 'descripcion' | 'veredicto' | 'faq' | 'seo' | 'other' {
    const t = title.toLowerCase();
    if (/criterio|editorial/.test(t)) return 'editorial';
    if (/descripci/.test(t)) return 'descripcion';
    if (/veredicto/.test(t)) return 'veredicto';
    if (/faq|preguntas/.test(t)) return 'faq';
    if (/seo/.test(t)) return 'seo';
    return 'other';
}

function parseFaq(block: string): CatalogFaqItem[] {
    const items: CatalogFaqItem[] = [];
    const qaRe = /(?:^|\n)[-\*]?\s*(?:P|Pregunta)\s*:\s*(.+?)\s*(?:\n)\s*(?:R|Respuesta)\s*:\s*([\s\S]*?)(?=(?:\n[-\*]?\s*(?:P|Pregunta)\s*:)|\s*$)/gi;
    let match: RegExpExecArray | null;
    while ((match = qaRe.exec(block)) !== null) {
        items.push({ q: match[1].trim(), a: match[2].trim() });
    }
    if (items.length) return items;

    const h3 = block.split(/^###\s+/m).slice(1);
    for (const part of h3) {
        const nl = part.indexOf('\n');
        const q = (nl === -1 ? part : part.slice(0, nl)).trim();
        const a = (nl === -1 ? '' : part.slice(nl + 1)).trim();
        if (q && a) items.push({ q, a });
    }
    return items;
}

function stripHtml(html: string): string {
    return html
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/h[1-6]>/gi, '\n\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

function patchFromAttrs(attrs: Record<string, string>): CatalogContentPatch {
    const patch: CatalogContentPatch = {};
    const map: Record<string, keyof CatalogContentPatch> = {
        nombre: 'nombre',
        title: 'nombre',
        marca: 'marca',
        descripcion_corta: 'descripcion_corta',
        descripcion_larga: 'descripcion_larga',
        veredicto_aipeces: 'veredicto_aipeces',
        veredicto: 'veredicto_aipeces',
        seo_title: 'seo_title',
        meta_title: 'seo_title',
        seo_description: 'seo_description',
        meta_description: 'seo_description',
        imagen_alt: 'imagen_alt',
        alt: 'imagen_alt',
        editorial: 'editorial',
        criterio: 'editorial',
    };
    for (const [key, field] of Object.entries(map)) {
        if (attrs[key]?.trim()) (patch[field] as string) = attrs[key].trim();
    }
    if (attrs.pesca_tags) patch.pesca_tags = parseScalarList(attrs.pesca_tags);
    if (attrs.badges) patch.badges = parseScalarList(attrs.badges);
    if (Object.prototype.hasOwnProperty.call(attrs, 'coleccion_slugs')) {
        const collections = readColeccionSlugs(attrs.coleccion_slugs);
        if (collections.error) throw new Error(collections.error);
        patch.coleccion_slugs = collections.slugs ?? [];
    }
    return patch;
}

function applyBodySections(body: string, patch: CatalogContentPatch, isHtml: boolean): void {
    const source = isHtml ? stripHtml(body) : body;
    if (!source) return;

    const parts = source.split(/^##\s+/m);
    if (parts.length === 1) {
        if (!patch.editorial) patch.editorial = source;
        return;
    }

    const preamble = parts[0].trim();
    if (preamble && !patch.editorial) patch.editorial = preamble;

    for (const part of parts.slice(1)) {
        const nl = part.indexOf('\n');
        const title = (nl === -1 ? part : part.slice(0, nl)).trim();
        const content = (nl === -1 ? '' : part.slice(nl + 1)).trim();
        if (!content) continue;
        const kind = headingKey(title);
        if (kind === 'editorial' && !patch.editorial) patch.editorial = content;
        else if (kind === 'descripcion' && !patch.descripcion_larga) patch.descripcion_larga = content;
        else if (kind === 'veredicto' && !patch.veredicto_aipeces) patch.veredicto_aipeces = content;
        else if (kind === 'faq') patch.faq = parseFaq(content);
        else if (kind === 'other' && !patch.editorial) patch.editorial = content;
    }
}

function leakFromPatch(patch: CatalogContentPatch): boolean {
    return textHasInternalLeak(
        [
            patch.nombre,
            patch.marca,
            patch.descripcion_corta,
            patch.descripcion_larga,
            patch.veredicto_aipeces,
            patch.seo_title,
            patch.seo_description,
            patch.imagen_alt,
            patch.editorial,
            (patch.pesca_tags || []).join(' '),
            (patch.badges || []).join(' '),
            (patch.faq || []).map((f) => `${f.q || f.question || ''} ${f.a || f.answer || ''}`).join('\n'),
            JSON.stringify(patch.specs || {}),
        ].join('\n'),
    );
}

export function parseProductFichaText(filename: string, raw: string): ParsedFichaFile {
    const warnings: string[] = [];
    try {
        const trimmed = raw.replace(/^\uFEFF/, '');
        if (filename.toLowerCase().endsWith('.json')) {
            const parsed = JSON.parse(trimmed) as unknown;
            const list = Array.isArray(parsed)
                ? parsed
                : parsed && typeof parsed === 'object' && Array.isArray((parsed as { products?: unknown }).products)
                    ? (parsed as { products: unknown[] }).products
                    : [parsed];
            if (list.length !== 1) {
                return {
                    filename,
                    seoSlug: '',
                    patch: {},
                    warnings: ['Este JSON tiene varios productos: usa parseProductFichaJsonBatch'],
                    error: 'JSON de lote: parsear con parseProductFichaJsonBatch',
                    leak: false,
                };
            }
            return parseJsonProduct(filename, list[0]);
        }

        const { attrs, body } = splitFrontmatter(trimmed);
        const seoSlug = (attrs.seo_slug || attrs.slug || '').trim();
        const patch = patchFromAttrs(attrs);
        applyBodySections(body, patch, filename.toLowerCase().endsWith('.html') || /<\/?[a-z][\s\S]*>/i.test(body));
        if (!seoSlug) warnings.push('Sin seo_slug en el frontmatter');
        const nuevo = readNuevoSeoSlug(attrs.nuevo_seo_slug, seoSlug);
        const leak = leakFromPatch(patch);
        if (leak) warnings.push('Contiene notas internas (CHAT/VB/pasada)');
        return {
            filename,
            seoSlug,
            nuevoSeoSlug: nuevo.slug,
            patch,
            warnings,
            leak,
            error: nuevo.error || (seoSlug ? undefined : 'Falta seo_slug'),
        };
    } catch (e) {
        return {
            filename,
            seoSlug: '',
            patch: {},
            warnings,
            leak: false,
            error: e instanceof Error ? e.message : String(e),
        };
    }
}

function parseJsonProduct(filename: string, raw: unknown): ParsedFichaFile {
    if (!raw || typeof raw !== 'object') {
        return { filename, seoSlug: '', patch: {}, warnings: [], leak: false, error: 'JSON inválido' };
    }
    const obj = raw as Record<string, unknown>;
    const inner = obj.patch && typeof obj.patch === 'object' ? (obj.patch as Record<string, unknown>) : obj;
    const seoSlug = String(obj.seo_slug || obj.seoSlug || inner.seo_slug || '').trim();
    const nuevo = readNuevoSeoSlug(
        String(obj.nuevo_seo_slug || inner.nuevo_seo_slug || ''),
        seoSlug,
    );
    const patch: CatalogContentPatch = {};
    if (typeof inner.nombre === 'string') patch.nombre = inner.nombre;
    if (typeof inner.marca === 'string') patch.marca = inner.marca;
    if (typeof inner.descripcion_corta === 'string') patch.descripcion_corta = inner.descripcion_corta;
    if (typeof inner.descripcion_larga === 'string') patch.descripcion_larga = inner.descripcion_larga;
    if (typeof inner.veredicto_aipeces === 'string') patch.veredicto_aipeces = inner.veredicto_aipeces;
    if (typeof inner.seo_title === 'string') patch.seo_title = inner.seo_title;
    if (typeof inner.seo_description === 'string') patch.seo_description = inner.seo_description;
    if (typeof inner.imagen_alt === 'string') patch.imagen_alt = inner.imagen_alt;
    if (typeof inner.editorial === 'string') patch.editorial = inner.editorial;
    if (Array.isArray(inner.pesca_tags)) patch.pesca_tags = inner.pesca_tags.map((x) => String(x));
    if (Array.isArray(inner.badges)) patch.badges = inner.badges.map((x) => String(x));
    if (Array.isArray(inner.faq)) patch.faq = inner.faq as CatalogFaqItem[];
    if (inner.specs && typeof inner.specs === 'object' && !Array.isArray(inner.specs)) {
        patch.specs = Object.fromEntries(
            Object.entries(inner.specs as Record<string, unknown>).map(([k, v]) => [k, String(v ?? '')]),
        );
    }
    if (Object.prototype.hasOwnProperty.call(inner, 'coleccion_slugs') || Object.prototype.hasOwnProperty.call(obj, 'coleccion_slugs')) {
        const collections = readColeccionSlugs(inner.coleccion_slugs ?? obj.coleccion_slugs);
        if (collections.error) {
            return {
                filename,
                seoSlug,
                patch,
                warnings: [],
                leak: false,
                error: collections.error,
            };
        }
        patch.coleccion_slugs = collections.slugs ?? [];
    }
    const leak = leakFromPatch(patch);
    return {
        filename,
        seoSlug,
        nuevoSeoSlug: nuevo.slug,
        patch,
        warnings: leak ? ['Contiene notas internas (CHAT/VB/pasada)'] : [],
        leak,
        error: nuevo.error || (seoSlug ? undefined : 'Falta seo_slug'),
    };
}

export function parseProductFichaJsonBatch(filename: string, raw: string): ParsedFichaFile[] {
    const parsed = JSON.parse(raw.replace(/^\uFEFF/, '')) as unknown;
    const list = Array.isArray(parsed)
        ? parsed
        : parsed && typeof parsed === 'object' && Array.isArray((parsed as { products?: unknown }).products)
            ? (parsed as { products: unknown[] }).products
            : [parsed];
    return list.map((item, i) => parseJsonProduct(`${filename}#${i + 1}`, item));
}

export function parsedToBatchItem(parsed: ParsedFichaFile) {
    return {
        seo_slug: parsed.seoSlug,
        filename: parsed.filename,
        patch: parsed.patch,
        ...(parsed.nuevoSeoSlug ? { nuevo_seo_slug: parsed.nuevoSeoSlug } : {}),
    };
}
